import ApiService from "../../serverConnection/ApiService.js"

import LanguageManager from "../../LanguageManager.js";
import UserProfile from "../../serverConnection/UserProfile.js";

/**
 * @author Corentin (ccharton) Charton
 * @description This is a public deployment: guests are the only accounts, so the profile
 * tab is reduced to viewing and editing the display name. Profile picture, status sentence,
 * username, email, password and 2FA all lived behind endpoints that no longer exist
 * server-side.
 */
class ProfileView {
    #applyModificationOnEnterBind = this.applyModificationOnEnter.bind(this)
    #graphemeInputListenerBind = this.graphemeInputListener.bind(this)
    #maxInputLength = 12
    #stringSegmenter = null
    /**
     * @author Corentin (ccharton) Charton
     * @description Constructor for the ProfileView class
     */
    constructor() {
        this.#stringSegmenter = new Intl.Segmenter(undefined, { granularity: 'grapheme' })

        const mutationObserver = new MutationObserver((_, observer) => {
            const root = document.getElementById('profileTabCenterMenu');
            if (root) {
                    this.hydrate();
                observer.disconnect();
            }
        });
        mutationObserver.observe(document.body, { childList: true, subtree: true });

        window.addEventListener("profileUpdated", () => {
            if (document.getElementById('profileTabCenterMenu')) {
                this.hydrate();
            }
        });
    }

    /**
     * @author Sonam (scrumier) Crumiere
     * Edited by: Corentin (ccharton) Charton
     * @description Hydrate the profile view with the backend data
     */
    hydrate() {
        UserProfile.updateProfileIfNeeded()

        const headerDisplayName = document.getElementById('profileHeaderDisplayName')
        const displayName = UserProfile.getDisplayName()

        if (headerDisplayName) {
            headerDisplayName.innerText = displayName;
        }

        const currentDisplayName = document.getElementById('profileCurrentDisplayNameValue')
        if (currentDisplayName) {
            currentDisplayName.innerText = displayName;
        }

        const picURL = UserProfile.getProfilePicture()
        if (picURL) {
            const profilePic = document.getElementById('profileProfilePicture')
            if (profilePic) {
                profilePic.src = picURL;
            }

            const footerPic = document.getElementById('footerMenuProfilePicture')
            if (footerPic) {
                footerPic.src = picURL;
            }

            const centerPic = document.getElementById('centerMenuProfilePicture')
            if (centerPic) {
                centerPic.src = picURL;
            }
        }
    }

    /**
     * @author Corentin (ccharton) Charton
     * @description Close the popup and reset the input
     */
    closePopup() {
        const popupTextInput = document.getElementById('popupTextInput')
        if (popupTextInput) {
            popupTextInput.value = ''
            popupTextInput.type = 'text'
            popupTextInput.removeAttribute('maxlength')
            popupTextInput.removeAttribute('minlength')
            popupTextInput.removeEventListener('input', this.#graphemeInputListenerBind)
        }

        document.getElementById('popupTextContainer')?.classList.add('hidden')
        document.getElementById('popupInvalidInput')?.classList.add('hidden')

        const popupApplyButton = document.getElementById('popupApplyButton')
        if (popupApplyButton) {
            popupApplyButton.dataset.i18n = 'popupApplyButton'
            popupApplyButton.classList.add('popupApplyButton')
            popupApplyButton.classList.remove('disableOTP')
            popupApplyButton.classList.remove('enableOTP')
        }

        const modifyPopUp = document.getElementById('modifyPopUp')
        if (modifyPopUp) {
            modifyPopUp.removeEventListener('keydown', this.#applyModificationOnEnterBind)
            modifyPopUp.classList.add('hidden')
            modifyPopUp.classList.remove('popupInCropMode')
        }
    }

    /**
     * @author Corentin (ccharton) Charton
     * @description Clear the popup text input
     */
    clearPopupTextInput() {
        const popupTextInput = document.getElementById('popupTextInput')
        if (popupTextInput) {
            popupTextInput.value = ''
            popupTextInput.focus()
        }
    }

    /**
     * @author Corentin (ccharton) Charton
     * @description Focus the popup text input when clicking on the outer div
     */
    focusPopupTextInput() {
        document.getElementById('popupTextInput')?.focus()
    }

    graphemeInputListener(event) {
        const targetValue = event.target.value
        const graphemes = Array.from(this.#stringSegmenter.segment(targetValue), s => s.segment);

        if (graphemes.length > this.#maxInputLength) {
            event.target.value = graphemes.slice(0, this.#maxInputLength).join('');
        }
    }

    /**
     * @author Corentin (ccharton) Charton
     * @description Open the popup to edit the display name
     */
    editDisplayName() {
        if (!document.getElementById('modifyPopUp')?.classList.contains('hidden')) {
            this.closePopup()
        }

        const modifyPopUpTitle = document.getElementById('modifyPopUpTitle')
        if (modifyPopUpTitle) {
            modifyPopUpTitle.dataset.i18n = 'popupEditDisplayNameTitle'
        }

        const popupDescription = document.getElementById('popupDescription')
        if (popupDescription) {
            popupDescription.dataset.i18n = 'popupEditDisplayNameDescription'
        }

    const popupLabelTextInput = document.getElementById('popupLabelTextInput')
        if (popupLabelTextInput) {
            popupLabelTextInput.dataset.i18n = 'popupEditDisplayNameLabelText'
        }

        const popupTextInput = document.getElementById('popupTextInput')
        if (popupTextInput) {
            popupTextInput.value = UserProfile.getDisplayName()
            popupTextInput.addEventListener('input', this.#graphemeInputListenerBind)
            this.#maxInputLength = 12
        }

        const popuInputIcon = document.getElementById('popupInputIcon')
        if (popuInputIcon) {
            popuInputIcon.src = "/img/overlay/center/loginRegister/user.svg"
        }

        const popupApplyButton = document.getElementById('popupApplyButton')
        if (popupApplyButton) {
            popupApplyButton.dataset.action = 'applyDisplayNameModification'
        }

        const modifyPopUp = document.getElementById('modifyPopUp')
        if (modifyPopUp) {
            modifyPopUp.classList.remove('hidden')
            modifyPopUp.addEventListener('keydown', this.#applyModificationOnEnterBind)
        }

        document.getElementById('popupTextContainer')?.classList.remove('hidden')
        if (popupTextInput) {
            popupTextInput.focus()
        }

        LanguageManager.updateContent()
    }

    /**
     * @author Corentin (ccharton) Charton
     * @description Apply the modification when pressing enter on text based popups
     * @param e {KeyboardEvent} - The event triggered by the keydown
     */
    applyModificationOnEnter(e) {
        if (e.key === 'Enter') {
            document.getElementById('popupApplyButton')?.click()
        }
    }

    /**
     * @author Corentin (ccharton) Charton
     * @description Apply the modification on the display name
     */
    async applyDisplayNameModification() {
        let newDisplayName = document.getElementById('popupTextInput')?.value
        if (newDisplayName) {
            try {
                await ApiService.updateProfile({ displayName: newDisplayName })
                UserProfile.setDisplayName(newDisplayName)
                document.getElementById('profileCurrentDisplayNameValue').innerText = newDisplayName
                document.getElementById('profileHeaderDisplayName').innerText = newDisplayName
                document.getElementById('centerMenuHeaderDisplayName').innerText = newDisplayName
                this.closePopup()
            } catch (e) {
                const popupInvalidInput = document.getElementById('popupInvalidInput')
                if (popupInvalidInput) {
                    if (e.message === 'PROFILE_DISPLAY_NAME_INVALID' || e.message === 'PROFILE_DISPLAY_NAME_TOO_LONG') {
                        popupInvalidInput.dataset.i18n = e.message
                    } else {
                        console.error(e)
                    }
                    popupInvalidInput.classList.remove('hidden')
                    LanguageManager.updateContent()
                }
                document.getElementById('popupTextInput')?.focus()
            }
        }
    }

}

export default new ProfileView()
